import { useSectionId } from '@v4/components/common/SectionItem'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import AppStore from '@v4/store/appStore'
import QuizQuestionsSelector from '@v4/utils/selectors/QuizQuestionsSelector'
import { useMemo } from 'react'
import { parseScorePercent } from '@v4/utils'
import { MERGE_TAG_GROUP } from '@v4/hooks/useEditor'
import { SCORE_FORMAT } from '@v4/constants/result'
import PageData from '@v4/utils/data/PageData'
import { getFormattedScore } from '@v4/utils/format'

const useCategoryScoreMergeTags = () => {
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const pageType = PageBuilderSelector.useType()
	const sectionId = useSectionId()
	const categoryIds = QuizQuestionsSelector.useCategoryIds()
	const categoryEntities = QuizQuestionsSelector.useCategoryEntities()
	const resultScores = AppStore.useStoreState(state => state.result.scores)
	const categoryTiers = PageBuilderSelector.useCategoryTierIds({ sectionId, group: 'category' })

	return useMemo(() => {
		if (!isBuilderMode || (!PageData.isTypeResult(pageType) && !PageData.isTypeResultPdf(pageType))) {
			return undefined
		}
		return _.reduce(
			categoryIds,
			(acc, id, index) => {
				const category = categoryEntities[id]
				const categoryTierId = categoryTiers[id]
				const categoryScores = resultScores[id]
				const score = categoryScores?.[categoryTierId || 'default']

				if (!category || !score) {
					return acc
				}

				const { ref, title } = category

				const group = `score:${ref}`

				acc.groups[group] = {
					title,
					order: index + 1,
				}

				acc.tags[`score.${ref}.tier`] = {
					title: 'Tier',
					searchLabel: `Scores | ${title} | Tier`,
					label: `${title} | Tier`,
					groups: [MERGE_TAG_GROUP.SCORES, group],
					order: 1,
				}

				acc.tags[`score.${ref}.percent`] = {
					title: 'Percent',
					searchLabel: `Scores | ${title} | Percent`,
					label: `${title} | Percent`,
					groups: [MERGE_TAG_GROUP.SCORES, group],
					order: 2,
				}

				acc.tags[`score.${ref}.actual`] = {
					title: 'Actual',
					searchLabel: `Scores | ${title} | Actual`,
					label: `${title} | Actual`,
					groups: [MERGE_TAG_GROUP.SCORES, group],
					order: 3,
				}

				acc.tags[`score.${ref}.out_of_10`] = {
					title: 'Out of 10',
					searchLabel: `Scores | ${title} | Out of 10`,
					label: `${title} | Out of 10`,
					groups: [MERGE_TAG_GROUP.SCORES, group],
					order: 4,
				}

				acc.tags[`score.${ref}.out_of_10_rounded`] = {
					title: 'Out of 10 rounded',
					searchLabel: `Scores | ${title} | Out of 10 rounded`,
					label: `${title} | Out of 10 rounded`,
					groups: [MERGE_TAG_GROUP.SCORES, group],
					order: 5,
				}

				const percent = parseScorePercent(score.score_percent)

				acc.shortcuts[`score.${ref}.tier`] = score.name
				acc.shortcuts[`score.${ref}.percent`] = getFormattedScore(SCORE_FORMAT.PERCENTAGE, percent, score.score)
				acc.shortcuts[`score.${ref}.actual`] = getFormattedScore(
					SCORE_FORMAT.SCORE,
					percent,
					score.score,
				).toString()
				acc.shortcuts[`score.${ref}.out_of_10`] = getFormattedScore(
					SCORE_FORMAT.OUT_OF_10,
					percent,
					score.score,
				)
				acc.shortcuts[`score.${ref}.out_of_10_rounded`] = getFormattedScore(
					SCORE_FORMAT.OUT_OF_10_ROUNDED,
					percent,
					score.score,
				)

				return acc
			},
			{
				tags: {},
				groups: {},
				shortcuts: {},
			},
		)
	}, [categoryEntities, categoryIds, categoryTiers, isBuilderMode, pageType, resultScores])
}

export default useCategoryScoreMergeTags
